import { useEffect, useState } from 'react';
import calendlyLogo from '@/assets/images/calendly_logo.png';
import useIntegrationsStore from '@/stores/IntegrationsStore';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { saveIntegrationSettings } from '@/hooks/integrations';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { useCalendlyPopup } from '@/components/onboarding/CalendlyPopup';

const CalendlySettings = () => {
  const {
    calendly,
    loading: storeLoading,
    setCalendlyEventTypeUri,
  } = useIntegrationsStore(
    useShallow((state) => ({
      calendly: state.calendly,
      loading: state.loading,
      setCalendlyEventTypeUri: state.setCalendlyEventTypeUri,
    }))
  );
  const [selectedEventTypeUri, setSelectedEventTypeUri] = useState('');
  const [saving, setSaving] = useState(false);

  const { openCalendlyPopup, loading: popupLoading } = useCalendlyPopup();

  // Determine connection status based on calendly data
  const getConnectionStatus = () => {
    if (!calendly) {
      return 'not_connected';
    }

    const isActive = calendly.active === true;
    const hasEventType = calendly.current_event_type_uri;

    // Pending: isActive is true but hasEventType is null/falsy
    if (isActive && !hasEventType) {
      return 'pending';
    }

    // Connected: isActive is true and hasEventType exists
    if (isActive && hasEventType) {
      return 'connected';
    }

    // Not connected: isActive is false
    return 'not_connected';
  };

  const connectionStatus = getConnectionStatus();

  // Set selected event type from store
  useEffect(() => {
    if (calendly?.current_event_type_uri) {
      setSelectedEventTypeUri(calendly.current_event_type_uri);
    }
  }, [calendly]);

  const handleConnect = async () => {
    await openCalendlyPopup();
  };

  const handleSaveEventType = async () => {
    if (!selectedEventTypeUri) {
      toast.error('Please select an event type');
      return;
    }

    // Find the selected event type to get location kind
    const eventTypes = calendly?.event_types || [];
    const selectedEventType = eventTypes.find(
      (et) => et.uri === selectedEventTypeUri
    );

    if (!selectedEventType) {
      toast.error('Selected event type not found');
      return;
    }

    setSaving(true);
    try {
      await saveIntegrationSettings('calendly', {
        calendly_event_type_uri: selectedEventTypeUri,
      });
      toast.success('Event type saved successfully');
      // Update store with the saved event type URI
      if (calendly) {
        setCalendlyEventTypeUri(selectedEventTypeUri);
      }
    } catch (error) {
      console.error('Failed to save event type:', error);
      toast.error(error.response?.data?.message || 'Failed to save event type');
    } finally {
      setSaving(false);
    }
  };

  if (storeLoading || popupLoading) {
    return (
      <div className='flex items-center justify-center pt-48'>
        <Loader2 className='h-8 w-8 animate-spin' />
      </div>
    );
  }

  const eventTypes = calendly?.event_types || [];
  const selectedEventType = eventTypes.find(
    (et) => et.uri === calendly?.current_event_type_uri
  );

  return (
    <div className='h-full space-y-6'>
      <div className='flex justify-between'>
        <div>
          <h3 className='text-lg font-medium'>Calendly Integration</h3>
          <p className='text-sm text-muted-foreground'>
            Connect your Calendly account to schedule meetings
          </p>
        </div>
      </div>
      <Separator />

      {connectionStatus === 'not_connected' ? (
        <div className='flex flex-col items-center justify-center pt-24'>
          <div className='mb-6 rounded-lg bg-gray-100 p-4'>
            <img
              src={calendlyLogo}
              alt='Calendly'
              className='h-12 w-12 object-contain'
            />
          </div>
          <h2 className='mb-2 text-2xl font-bold'>Connect Calendly</h2>
          <p className='mb-6 text-center text-muted-foreground'>
            Connect your Calendly account to start scheduling meetings
          </p>
          <Button onClick={handleConnect} disabled={popupLoading}>
            {popupLoading ? (
              <>
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                Connecting...
              </>
            ) : (
              'Connect Calendly'
            )}
          </Button>
        </div>
      ) : (
        <div className='space-y-6'>
          {/* Connection Status */}
          <Card>
            <CardHeader>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-3'>
                  <div className='rounded-lg bg-gray-100 p-2'>
                    <img
                      src={calendlyLogo}
                      alt='Calendly'
                      className='h-6 w-6 object-contain'
                    />
                  </div>
                  <div>
                    <CardTitle>Calendly</CardTitle>
                    <CardDescription>
                      {connectionStatus === 'pending'
                        ? 'Setup incomplete'
                        : 'Connected'}
                    </CardDescription>
                  </div>
                </div>
                {connectionStatus === 'pending' ? (
                  <div className='flex items-center gap-2 rounded-full bg-yellow-50 px-3 py-1'>
                    <AlertCircle className='h-4 w-4 text-yellow-600' />
                    <span className='text-sm font-medium text-yellow-600'>
                      Pending
                    </span>
                  </div>
                ) : (
                  <div className='flex items-center gap-2 rounded-full bg-green-50 px-3 py-1'>
                    <CheckCircle2 className='h-4 w-4 text-green-600' />
                    <span className='text-sm font-medium text-green-600'>
                      Connected
                    </span>
                  </div>
                )}
              </div>
            </CardHeader>
          </Card>

          {/* Event Type Selection */}
          <Card>
            <CardHeader>
              <CardTitle>Event Type</CardTitle>
              <CardDescription>
                Select the event type to use for scheduling meetings
              </CardDescription>
            </CardHeader>
            <CardContent className='space-y-4'>
              {selectedEventType && (
                <div className='rounded-md bg-muted p-3'>
                  <p className='text-sm font-medium text-foreground'>
                    Current Event Type: {selectedEventType.name}
                    {selectedEventType.duration && (
                      <span className='ml-2 text-muted-foreground'>
                        ({selectedEventType.duration} minutes)
                      </span>
                    )}
                  </p>
                </div>
              )}
              {eventTypes.length === 0 ? (
                <Alert>
                  <AlertDescription>
                    No event types found. Please create an event type in your
                    Calendly account.
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  <Select
                    value={selectedEventTypeUri}
                    onValueChange={setSelectedEventTypeUri}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='Select an event type' />
                    </SelectTrigger>
                    <SelectContent>
                      {eventTypes.map((eventType) => (
                        <SelectItem key={eventType.uri} value={eventType.uri}>
                          <div className='flex flex-col'>
                            <span className='font-medium'>
                              {eventType.name}
                            </span>
                            {eventType.duration && (
                              <span className='text-xs text-muted-foreground'>
                                {eventType.duration} minutes
                              </span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {connectionStatus === 'pending' && (
                    <Alert>
                      <AlertDescription>
                        Please select an event type to complete the setup.
                      </AlertDescription>
                    </Alert>
                  )}
                  <Button
                    onClick={handleSaveEventType}
                    disabled={!selectedEventTypeUri || saving}
                  >
                    {saving ? (
                      <>
                        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                        Saving...
                      </>
                    ) : (
                      'Save Event Type'
                    )}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CalendlySettings;
